import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Briefcase, Languages, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLang } from '@/i18n/LanguageContext';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const handleNav = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <section id="top" className="relative isolate pt-24 md:pt-40 pb-12 md:pb-24 overflow-hidden bg-[#f4fcf9]">
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#fffbf0] via-[#f2faf7] to-[#e6f7f4] opacity-80" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">

          {/* Text Content */}
          <div className="lg:col-span-6 lg:pr-8">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-[10px] sm:text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-4 sm:mb-6"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {t.hero.eyebrow}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="text-3xl sm:text-[2.75rem] leading-[1.1] md:text-6xl lg:text-[4rem] font-extrabold tracking-tight text-[#111827] mb-4 sm:mb-6"
            >
              {t.hero.title1}<br />
              <span className="relative inline-block mt-1 sm:mt-2">
                <span className="relative z-10 text-brand-primary">
                  {t.hero.title2}
                </span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-base sm:text-lg md:text-[19px] text-slate-600 font-medium leading-relaxed mb-6 sm:mb-8 max-w-xl"
            >
              {lang === 'en'
                ? 'Hands-On Training • Job Placement Support • Flexible Schedules • In-House Payment Plans • Graduate Debt-Free • Bilingual Programs'
                : 'Práctica Clínica • Apoyo de Colocación • Horarios Flexibles • Planes de Pago Internos • Gradúate Sin Deudas • Programas Bilingües'}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <Button
                onClick={() => handleNav('/application')}
                className="bg-brand-primary hover:opacity-80 text-white rounded-full px-6 sm:px-8 h-12 sm:h-14 text-sm sm:text-base font-semibold shadow-lg shadow-teal-900/20 group w-full sm:w-auto"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <Button
                onClick={() => window.location.href = 'tel:+17047410908'}
                variant="outline"
                className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700 rounded-full px-6 sm:px-8 h-12 sm:h-14 text-sm sm:text-base font-semibold shadow-sm w-full sm:w-auto"
              >
                <Phone className="w-4 h-4 mr-2 text-slate-400" />
                {t.hero.ctaSecondary}
              </Button>
            </motion.div>
          </div>

          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="lg:col-span-6 relative mt-8 lg:mt-0"
          >
            <div className="relative rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden shadow-2xl">
              <img
                src="/images/hero_group_2026.jpeg"
                alt="Dental Assistant Training"
                className="w-full h-auto aspect-[4/3] sm:aspect-[3/2] object-cover object-center"
              />

              {/* Open badge */}
              <div className="absolute top-4 left-3 sm:top-6 sm:left-6 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white shadow-xl border border-slate-100 flex items-center gap-2 z-10">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs sm:text-sm font-bold text-slate-700">{t.schedule.labels.open}</span>
              </div>

              {/* Stats card */}
              <div className="absolute bottom-2 left-2 right-2 sm:bottom-4 sm:left-4 sm:right-4 bg-white/85 backdrop-blur-lg rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-2xl border border-white shadow-teal-900/10 z-10">
                <div className="grid grid-cols-2 gap-y-3 gap-x-2 sm:gap-y-4 sm:gap-x-4">
                  <div className="text-center border-r border-slate-300/50 border-b pb-2 sm:pb-3 border-b-slate-300/50">
                    <div className="text-lg sm:text-2xl font-black text-[#1a4a4a]">{t.hero.stats.graduatesNum}</div>
                    <div className="text-[8px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5 sm:mt-1 leading-tight">{t.hero.stats.graduatesLabel}</div>
                  </div>
                  <div className="text-center border-b border-b-slate-300/50 pb-2 sm:pb-3">
                    <div className="text-lg sm:text-2xl font-black text-[#1a4a4a]">{t.hero.stats.weeksNum}</div>
                    <div className="text-[8px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5 sm:mt-1 leading-tight">{t.hero.stats.weeksLabel}</div>
                  </div>
                  <div className="text-center border-r border-slate-300/50 pt-1 sm:pt-2 flex flex-col items-center justify-center">
                    <CreditCard className="w-4 sm:w-5 h-4 sm:h-5 text-[#1a4a4a] mb-1 sm:mb-1.5" />
                    <div className="text-[8px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-tight">{t.hero.stats.placementLabel}</div>
                  </div>
                  <div className="text-center pt-1 sm:pt-2 flex flex-col items-center justify-center">
                    <Languages className="w-4 sm:w-5 h-4 sm:h-5 text-[#1a4a4a] mb-1 sm:mb-1.5" />
                    <div className="text-[8px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-tight">{t.hero.stats.langLabel}</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
